import React, { useState } from "react";
import { NetworkRequestGate } from "../../../../../common/components/NetworkRequestGate";
import { useActionRequest } from "../../../../../common/hooks/api";
import { useSurvey } from "../../../../../common/hooks/api/useSurvey";
import { Organization } from "../../../../../common/models";
import { Api } from "../../../../../common/service/Api";
import { OrganizationDataRecipientDto } from "../../../../../common/service/server/dto/OrganizationDataRecipientDto";
import {
  DataRecipientDisclosureSettings,
  finishedDisclosure,
  saveDisclosureUpdates,
  useDataRecipientDisclosureSettings,
} from "../../../../components/organisms/data-recipients/DataRecipientDisclosureSettings";
import { SettingsIndicator } from "../../../../components/organisms/data-recipients/SettingsIndicator";
import { isThirdPartyRecipient } from "../../../../util/vendors";
import { SELLING_AND_SHARING_SURVEY_NAME } from "../../../get-compliant/data-recipients/shared";
import {
  useDataDisclosureItems,
  useManagedAdNetworks,
} from "../../../../../common/hooks/dataRecipientSettingsHooks";
import { EditTab } from "../EditTab";
import { UpdateVendorArgs } from "../workflows/create-data-recipient/ClassificationForDataRecipientPage";
import { useOrganizationVendors } from "../../../../hooks";

type DataDisclosureTabProps = {
  handleRefresh: () => Promise<void>;
  org: Organization;
  dataRecipient: OrganizationDataRecipientDto;
};

export const DataDisclosureSettingsTab: React.FC<DataDisclosureTabProps> = ({
  org,
  dataRecipient,
  handleRefresh,
}) => {
  const [editMode, setEditMode] = useState(false);

  const enableEditMode = () => {
    setEditMode(true);
  };

  const [orgVendors] = useOrganizationVendors(org.id, undefined, true);
  const { managedAdNetworks } = useManagedAdNetworks(org.id);

  const {
    answers,
    request: surveyRequest,
    mutators: { patchAnswer },
  } = useSurvey(org.id, SELLING_AND_SHARING_SURVEY_NAME);

  const { fetch: updateDataRecipient } = useActionRequest({
    api: async (v: UpdateVendorArgs) => {
      const { vendorId, vendorSettings } = v;
      await Api.organizationVendor.addOrUpdateVendor(org.id, vendorId, vendorSettings);
    },
    onSuccess: handleRefresh,
  });

  const {
    state: disclosureState,
    meta: disclosureMeta,
    updateState: disclosureUpdate,
  } = useDataRecipientDisclosureSettings(
    dataRecipient,
    answers,
    org,
    dataRecipient?.isCustom,
    orgVendors,
    dataRecipient?.category,
  );

  const dataDisclosureSettingsItems = useDataDisclosureItems(
    org,
    dataRecipient,
    disclosureState,
    disclosureMeta,
    answers,
    managedAdNetworks,
  );

  const disableEditMode = async () => {
    await saveDisclosureUpdates(
      org,
      dataRecipient,
      answers,
      disclosureState,
      updateDataRecipient,
      patchAnswer,
    );

    setEditMode(false);
  };

  const finished = finishedDisclosure(disclosureState, disclosureMeta, org, false);

  return (
    <NetworkRequestGate requests={[surveyRequest]}>
      <EditTab
        isEditing={editMode}
        editLabel={"Edit Data Disclosure"}
        saveDisabled={!finished}
        saveDisabledLabel="Please select at least one processing region."
        onEnableEditing={enableEditMode}
        onDisableEditing={disableEditMode}
      >
        {editMode ? (
          <DataRecipientDisclosureSettings
            org={org}
            vendor={dataRecipient}
            isThirdParty={isThirdPartyRecipient(dataRecipient)}
            disclosureState={disclosureState}
            disclosureMeta={disclosureMeta}
            updateDisclosureState={disclosureUpdate}
          />
        ) : (
          <>
            {dataDisclosureSettingsItems.map((item) => {
              return <SettingsIndicator settingsItem={item} key={item.id} />;
            })}
          </>
        )}
      </EditTab>
    </NetworkRequestGate>
  );
};
